import { z, ZodSchema, ZodError } from 'zod';
import { EventEmitter } from 'events';
import path from 'path';
import fs from 'fs-extra';
import { IStorage, FileStorage, MemoryStorage, LayeredStorage, EncryptedStorage } from './storage';
import { logger } from '../utils/logger';

// 配置层级
export enum ConfigLayer {
  DEFAULTS = 'defaults',
  SYSTEM = 'system',
  PROJECT = 'project',
  USER = 'user',
  ENVIRONMENT = 'environment',
  RUNTIME = 'runtime'
}

// 配置变更事件
export interface ConfigChangeEvent {
  key: string;
  oldValue: any;
  newValue: any;
  layer: ConfigLayer;
}

// 配置验证结果
export interface ValidationResult {
  valid: boolean;
  errors?: Array<{
    path: string;
    message: string;
  }>;
}

// 配置管理器选项
export interface ConfigManagerOptions {
  schemas?: Record<string, ZodSchema>;
  storage?: IStorage;
  encryption?: {
    enabled: boolean;
    secretKey?: string;
  };
  projectRoot?: string;
  watch?: boolean;
}

// 配置变更处理器
export type ConfigChangeHandler = (event: ConfigChangeEvent) => void | Promise<void>;

export class ConfigManager extends EventEmitter {
  private configs: Map<ConfigLayer, Record<string, any>> = new Map();
  private schemas: Map<string, ZodSchema> = new Map();
  private storage: IStorage;
  private watchers: Map<string, Set<ConfigChangeHandler>> = new Map();
  private fileWatcher?: fs.FSWatcher;
  
  // 配置层优先级（从高到低）
  private readonly layerPriority: ConfigLayer[] = [
    ConfigLayer.RUNTIME,
    ConfigLayer.ENVIRONMENT,
    ConfigLayer.USER,
    ConfigLayer.PROJECT,
    ConfigLayer.SYSTEM,
    ConfigLayer.DEFAULTS
  ];

  constructor(private options: ConfigManagerOptions = {}) {
    super();
    
    // 初始化存储
    this.storage = this.initializeStorage();
    
    // 初始化 schemas
    if (options.schemas) {
      Object.entries(options.schemas).forEach(([key, schema]) => {
        this.schemas.set(key, schema);
      });
    }
    
    // 初始化各层配置
    this.layerPriority.forEach(layer => {
      this.configs.set(layer, {});
    });
  }

  /**
   * 初始化存储
   */
  private initializeStorage(): IStorage {
    if (this.options.storage) {
      return this.options.storage;
    }

    const projectRoot = this.options.projectRoot || process.cwd();
    const configDir = path.join(projectRoot, '.workflow', 'config');

    // 创建分层存储
    const layers: IStorage[] = [
      new MemoryStorage(), // runtime 层
      new MemoryStorage(), // environment 层
      new FileStorage(path.join(configDir, 'user')),
      new FileStorage(path.join(configDir, 'project')),
      new FileStorage(path.join(configDir, 'system')),
      new MemoryStorage()  // defaults 层
    ];

    let storage: IStorage = new LayeredStorage(layers);

    // 如果启用加密
    if (this.options.encryption?.enabled) {
      const secretKey = this.options.encryption.secretKey || process.env.WORKFLOW_SECRET_KEY;
      if (!secretKey) {
        throw new Error('Encryption enabled but no secret key provided');
      }
      storage = new EncryptedStorage(storage, secretKey);
    }

    return storage;
  }

  /**
   * 加载配置
   */
  async load(): Promise<void> {
    // 加载环境变量
    this.loadEnvironmentVariables();

    // 从存储加载各层配置
    for (const layer of this.layerPriority) {
      if (layer === ConfigLayer.ENVIRONMENT || layer === ConfigLayer.RUNTIME) {
        continue; // 这些层不从存储加载
      }

      try {
        const config = await this.storage.get(layer) || {};
        this.configs.set(layer, config);
      } catch (error) {
        logger.warn(`Failed to load ${layer} config:`, error);
      }
    }

    // 启动文件监听
    if (this.options.watch) {
      this.startWatching();
    }
  }

  /**
   * 加载环境变量
   */
  private loadEnvironmentVariables(): void {
    const envConfig: Record<string, any> = {};
    
    // 查找 WORKFLOW_ 前缀的环境变量
    Object.entries(process.env).forEach(([key, value]) => {
      if (key.startsWith('WORKFLOW_')) {
        const configKey = key
          .substring('WORKFLOW_'.length)
          .toLowerCase()
          .replace(/_/g, '.');
        
        // 将点分隔的键转换为嵌套对象
        const keys = configKey.split('.');
        let current = envConfig;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (!(keys[i] in current)) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        
        // 尝试解析 JSON
        try {
          current[keys[keys.length - 1]] = JSON.parse(value!);
        } catch {
          current[keys[keys.length - 1]] = value;
        }
      }
    });

    this.configs.set(ConfigLayer.ENVIRONMENT, envConfig);
  }

  /**
   * 获取配置值
   */
  get<T = any>(key: string, defaultValue?: T): T {
    // 按优先级查找
    for (const layer of this.layerPriority) {
      const layerConfig = this.configs.get(layer);
      if (layerConfig && this.hasNested(layerConfig, key)) {
        return this.getNested(layerConfig, key);
      }
    }

    return defaultValue as T;
  }

  /**
   * 设置配置值
   */
  async set(key: string, value: any, layer: ConfigLayer = ConfigLayer.RUNTIME): Promise<void> {
    // 验证 schema
    if (this.schemas.has(key)) {
      const schema = this.schemas.get(key)!;
      try {
        value = schema.parse(value);
      } catch (error) {
        if (error instanceof ZodError) {
          throw new Error(`Validation failed for "${key}": ${error.errors[0].message}`);
        }
        throw error;
      }
    }

    const layerConfig = this.configs.get(layer) || {};
    const oldValue = this.getNested(layerConfig, key);
    
    // 设置值
    this.setNested(layerConfig, key, value);
    this.configs.set(layer, layerConfig);

    // 持久化（除了 runtime 和 environment 层）
    if (layer !== ConfigLayer.RUNTIME && layer !== ConfigLayer.ENVIRONMENT) {
      await this.storage.set(layer, layerConfig);
    }

    // 触发变更事件
    const event: ConfigChangeEvent = { key, oldValue, newValue: value, layer };
    this.emit('change', event);
    this.emit(`change:${key}`, event);

    // 调用监听器
    const handlers = this.watchers.get(key);
    if (handlers) {
      for (const handler of handlers) {
        try {
          await handler(event);
        } catch (error) {
          logger.error(`Config change handler error for "${key}":`, error);
        }
      }
    }
  }

  /**
   * 删除配置值
   */
  async delete(key: string, layer?: ConfigLayer): Promise<void> {
    if (layer) {
      const layerConfig = this.configs.get(layer) || {};
      this.deleteNested(layerConfig, key);
      this.configs.set(layer, layerConfig);
      
      if (layer !== ConfigLayer.RUNTIME && layer !== ConfigLayer.ENVIRONMENT) {
        await this.storage.set(layer, layerConfig);
      }
    } else {
      // 从所有层删除
      for (const l of this.layerPriority) {
        await this.delete(key, l);
      }
    }
  }

  /**
   * 验证配置
   */
  validate(schema?: ZodSchema): ValidationResult {
    const config = this.getAll();
    const schemaToUse = schema || this.createCombinedSchema();

    try {
      schemaToUse.parse(config);
      return { valid: true };
    } catch (error) {
      if (error instanceof ZodError) {
        return {
          valid: false,
          errors: error.errors.map(e => ({
            path: e.path.join('.'),
            message: e.message
          }))
        };
      }
      throw error;
    }
  }

  /**
   * 监听配置变化
   */
  watch(pattern: string, callback: ConfigChangeHandler): () => void {
    const regex = new RegExp(pattern);
    
    // 为每个匹配的 key 添加监听器
    const handler: ConfigChangeHandler = (event) => {
      if (regex.test(event.key)) {
        callback(event);
      }
    };

    // 保存监听器
    if (!this.watchers.has(pattern)) {
      this.watchers.set(pattern, new Set());
    }
    this.watchers.get(pattern)!.add(callback);

    // 监听所有变更事件
    this.on('change', handler);

    // 返回取消函数
    return () => {
      this.off('change', handler);
      const handlers = this.watchers.get(pattern);
      if (handlers) {
        handlers.delete(callback);
        if (handlers.size === 0) {
          this.watchers.delete(pattern);
        }
      }
    };
  }

  /**
   * 导出配置
   */
  export(layer?: ConfigLayer): Record<string, any> {
    if (layer) {
      return { ...this.configs.get(layer) || {} };
    }
    return this.getAll();
  }

  /**
   * 导入配置
   */
  async import(config: Record<string, any>, layer: ConfigLayer = ConfigLayer.RUNTIME): Promise<void> {
    // 递归处理嵌套对象，验证并设置配置
    const processObject = async (obj: any, prefix: string = '') => {
      for (const [key, value] of Object.entries(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // 递归处理嵌套对象
          await processObject(value, fullKey);
        } else {
          // 设置值，set 方法会进行验证
          try {
            await this.set(fullKey, value, layer);
          } catch (error: any) {
            throw new Error(`Import validation failed: ${error.message}`);
          }
        }
      }
    };
    
    await processObject(config);
  }

  /**
   * 获取所有配置（合并所有层）
   */
  getAll(): Record<string, any> {
    const result = {};
    
    // 按优先级反向合并（低优先级先）
    for (let i = this.layerPriority.length - 1; i >= 0; i--) {
      const layer = this.layerPriority[i];
      const layerConfig = this.configs.get(layer) || {};
      Object.assign(result, layerConfig);
    }

    return result;
  }

  /**
   * 重置配置
   */
  async reset(layer?: ConfigLayer): Promise<void> {
    if (layer) {
      this.configs.set(layer, {});
      if (layer !== ConfigLayer.RUNTIME && layer !== ConfigLayer.ENVIRONMENT) {
        await this.storage.delete(layer);
      }
    } else {
      for (const l of this.layerPriority) {
        await this.reset(l);
      }
    }
  }

  /**
   * 启动文件监听
   */
  private startWatching(): void {
    const configDir = path.join(this.options.projectRoot || process.cwd(), '.workflow', 'config');
    
    if (!fs.existsSync(configDir)) {
      return;
    }

    this.fileWatcher = fs.watch(configDir, { recursive: true }, async (eventType, filename) => {
      if (!filename || !filename.endsWith('.json')) {
        return;
      }

      logger.debug(`Config file changed: ${filename}`);
      
      // 重新加载配置
      try {
        await this.load();
        this.emit('reload');
      } catch (error) {
        logger.error('Failed to reload config:', error);
      }
    });
  }

  /**
   * 停止文件监听
   */
  stopWatching(): void {
    if (this.fileWatcher) {
      this.fileWatcher.close();
      this.fileWatcher = undefined;
    }
  }

  /**
   * 清理资源
   */
  async dispose(): Promise<void> {
    this.stopWatching();
    this.removeAllListeners();
    this.watchers.clear();
  }

  // 辅助方法
  private hasNested(obj: any, path: string): boolean {
    const keys = path.split('.');
    let current = obj;
    
    for (const key of keys) {
      if (!current || typeof current !== 'object' || !(key in current)) {
        return false;
      }
      current = current[key];
    }
    
    return true;
  }

  private getNested(obj: any, path: string): any {
    const keys = path.split('.');
    let current = obj;
    
    for (const key of keys) {
      if (current == null || typeof current !== 'object') {
        return undefined;
      }
      current = current[key];
    }
    
    return current;
  }

  private setNested(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }
    
    current[keys[keys.length - 1]] = value;
  }

  private deleteNested(obj: any, path: string): void {
    const keys = path.split('.');
    let current = obj;
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current)) {
        return;
      }
      current = current[key];
    }
    
    delete current[keys[keys.length - 1]];
  }

  private createCombinedSchema(): ZodSchema {
    // 合并所有注册的 schemas
    const shape: Record<string, ZodSchema> = {};
    
    this.schemas.forEach((schema, key) => {
      shape[key] = schema;
    });
    
    return z.object(shape).partial();
  }

  private createSchemaForObject(obj: any): ZodSchema {
    // 动态创建 schema
    return z.object({}).passthrough();
  }
}

// 默认配置 schemas
export const defaultSchemas = {
  'project.name': z.string().min(1),
  'project.version': z.string().regex(/^\d+\.\d+\.\d+$/),
  'settings.autoSync': z.boolean(),
  'settings.syncInterval': z.number().min(1000),
  'settings.enableAI': z.boolean(),
  'settings.checkMode': z.enum(['quick', 'normal', 'full'])
};